import React from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Home, Wrench, Shield, Droplets, Sparkles, Phone, ArrowRight, CheckCircle, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

const ServicesPage = () => {
  const services = [
    {
      id: 1,
      title: "Réfection & rénovation de toiture",
      description: "Rénovation complète de votre toiture avec matériaux de qualité et respect des DTU. Diagnostic complet, dépose de l'ancien, pose du nouveau revêtement.",
      icon: Home,
      features: [
        "Diagnostic sur site avec rapport détaillé",
        "Dépose de l'ancien revêtement",
        "Vérification et renforcement charpente",
        "Pose écran sous-toiture HPV",
        "Installation nouveau revêtement",
        "Finitions : rives, faîtage, ventilation"
      ],
      duration: "3 à 7 jours selon surface",
      warranty: "10 ans"
    },
    {
      id: 2,
      title: "Zinguerie (zinc, noues, abergements)",
      description: "Pose, réparation et rénovation de vos éléments de zinguerie en zinc, aluminium ou acier laqué sur mesure.",
      icon: Wrench,
      features: [
        "Gouttières demi-ronde et corniche",
        "Chéneaux et noues sur mesure",
        "Abergements cheminées et souches",
        "Solins et bavettes étanches",
        "Descentes pluviales",
        "Soudures professionnelles"
      ],
      duration: "1 à 3 jours",
      warranty: "5 ans"
    },
    {
      id: 3,
      title: "Couverture tuiles/ardoises/bac acier",
      description: "Installation de tous types de couverture adaptés à votre charpente, pente et environnement local.",
      icon: Shield,
      features: [
        "Tuiles mécaniques et canal",
        "Ardoises naturelles et fibres-ciment",
        "Bac acier anti-condensation",
        "Lattage et contre-lattage adaptés",
        "Ventilation faîtage et égouts",
        "Accessoires assortis"
      ],
      duration: "2 à 5 jours",
      warranty: "10 ans"
    },
    {
      id: 4,
      title: "Étanchéité toit-terrasse (EPDM/bitume)",
      description: "Solutions d'étanchéité durables pour toitures plates et terrasses avec membrane EPDM ou bitume.",
      icon: Droplets,
      features: [
        "Diagnostic étanchéité existante",
        "Préparation supports et pentes",
        "Pose membrane EPDM ou bitume",
        "Relevés et points singuliers",
        "Évacuations dimensionnées",
        "Finitions couvertines"
      ],
      duration: "2 à 4 jours",
      warranty: "10 ans"
    },
    {
      id: 5,
      title: "Nettoyage, démoussage & hydrofuge",
      description: "Entretien professionnel de votre toiture pour prolonger sa durée de vie et prévenir les infiltrations.",
      icon: Sparkles,
      features: [
        "Inspection préalable détaillée",
        "Nettoyage haute pression maîtrisée",
        "Traitement anti-mousse longue durée",
        "Application hydrofuge protecteur",
        "Vérification gouttières",
        "Contrôles finaux"
      ],
      duration: "1 jour",
      warranty: "2 ans"
    },
    {
      id: 6,
      title: "Dépannage & urgence 24/7",
      description: "Intervention rapide en urgence pour sécuriser votre toiture et limiter les dégâts en cas de sinistre.",
      icon: Phone,
      features: [
        "Astreinte 24h/24, 7j/7",
        "Bâchage et mise en sécurité",
        "Recherche et localisation fuites",
        "Documentation pour assurance",
        "Réparations provisoires",
        "Planification réparations définitives"
      ],
      duration: "Intervention immédiate",
      warranty: "1 an"
    }
  ];

  const handleCallClick = () => {
    window.location.href = 'tel:[TEL]';
  };

  return (
    <Layout>
      {/* Header Section */}
      <section className="hero section-sm">
        <div className="hero-content">
          <div className="container text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] mb-6">
              Nos services de couverture
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Découvrez notre gamme complète de prestations pour tous vos travaux de toiture, 
              zinguerie et étanchéité sur Toulouse et l'Occitanie.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <Card key={service.id} className="card-hover p-8 bg-white">
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 font-['Outfit'] mb-4">
                        {service.title}
                      </h3>
                      
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {service.description}
                      </p>
                      
                      <div className="space-y-6">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">Ce qui est inclus :</h4>
                          <ul className="space-y-2">
                            {service.features.map((feature, index) => (
                              <li key={index} className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                <span className="text-gray-600">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div className="flex flex-wrap gap-4">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center gap-2">
                            <Clock className="w-4 h-4 text-blue-600" />
                            <span className="text-blue-800 text-sm font-medium">{service.duration}</span>
                          </div>
                          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 flex items-center gap-2">
                            <Shield className="w-4 h-4 text-green-600" />
                            <span className="text-green-800 text-sm font-medium">Garantie {service.warranty}</span>
                          </div>
                        </div>
                        
                        <Link 
                          to="/contact"
                          className="btn btn-primary inline-flex"
                        >
                          Demander un devis
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quality Commitment */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <Card className="p-12 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              
              <h3 className="text-3xl font-bold text-gray-900 font-['Outfit'] mb-6">
                Notre engagement qualité
              </h3>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Chaque chantier respecte les DTU (Documents Techniques Unifiés) et bénéficie 
                de notre garantie décennale. Nous privilégions des matériaux de qualité 
                et un suivi personnalisé avec un interlocuteur unique du devis à la réception.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-2">DTU</div>
                  <div className="text-sm text-gray-600">Respect des normes</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-2">10 ans</div>
                  <div className="text-sm text-gray-600">Garantie décennale</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-2">24-48h</div>
                  <div className="text-sm text-gray-600">Délai de réponse</div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact" className="btn btn-primary">
                  Demander un devis gratuit
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <button 
                  onClick={handleCallClick}
                  className="btn btn-secondary"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Nous appeler
                </button>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ServicesPage;